import { useState, useEffect, useCallback, useRef } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import Icon from "@mui/material/Icon";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import useAPI from "utils/useApi";
import handsFriezeOrnament from "assets/images/imagine_maini_ridicate.png";
import rightCurveOverlay from "assets/images/curba_dreapta_transparenta_real.png";

const AUTOPLAY_INTERVAL = 6000;
/** Pentru video încorporat (iframe); nu putem citi evenimente „ended”. */
const EMBED_VIDEO_INTERVAL_MS = 90000;
/** Același prag ca navbarul — ≤1024px = layout mobile/tabletă. */
const DESKTOP_CAROUSEL_QUERY = "(min-width:1025px)";
/** Padding vertical simetric — zona text + CTA pe mobile/tabletă. */
const STACKED_TEXT_PY = { xs: 2.75, sm: 3 };

const NATIVE_VIDEO_RE = /\.(mp4|webm|ogg)(\?.*)?$/i;

function isNativeVideoFile(url = "") {
  return NATIVE_VIDEO_RE.test(url);
}

function getSlidePosterUrl(slide) {
  return slide?.thumbnail || slide?.image || slide?.poster || slide?.thumbnailUrl || null;
}

function getYouTubeVideoId(url = "") {
  try {
    const u = new URL(url, window.location.origin);
    if (u.hostname.includes("youtu.be")) {
      return u.pathname.slice(1).split("/")[0] || null;
    }
    if (u.hostname.includes("youtube.com") || u.hostname.includes("youtube-nocookie.com")) {
      if (u.pathname.startsWith("/embed/")) return u.pathname.split("/")[2] || null;
      return u.searchParams.get("v");
    }
  } catch {
    /* URL relativ sau invalid */
  }
  const embedMatch = url.match(/(?:embed\/|youtu\.be\/)([\w-]{11})/);
  if (embedMatch) return embedMatch[1];
  const vMatch = url.match(/[?&]v=([\w-]{11})/);
  return vMatch ? vMatch[1] : null;
}

function getVimeoVideoId(url = "") {
  try {
    const u = new URL(url, window.location.origin);
    const match = u.pathname.match(/\/(?:video\/)?(\d+)/);
    return match ? match[1] : null;
  } catch {
    const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return m ? m[1] : null;
  }
}

function captureNativeVideoFrame(src) {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.crossOrigin = "anonymous";

    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      video.removeAttribute("src");
      video.load();
      resolve(result);
    };

    const timeout = setTimeout(() => finish(null), 12000);

    video.addEventListener("error", () => finish(null));
    video.addEventListener("loadeddata", () => {
      const t =
        Number.isFinite(video.duration) && video.duration > 0
          ? Math.min(1, video.duration * 0.05)
          : 0.1;
      try {
        video.currentTime = t;
      } catch {
        finish(null);
      }
    });
    video.addEventListener("seeked", () => {
      try {
        const { videoWidth: w, videoHeight: h } = video;
        if (!w || !h) {
          finish(null);
          return;
        }
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(video, 0, 0, w, h);
        finish(canvas.toDataURL("image/jpeg", 0.82));
      } catch {
        finish(null);
      }
    });

    video.src = src;
  });
}

/** Thumbnail pentru slide video: poster din CMS, YouTube/Vimeo, prim cadru MP4, sau fallback. */
function CarouselVideoThumbnail({ slide }) {
  const videoUrl = slide?.videoUrl ?? "";
  const cmsPoster = getSlidePosterUrl(slide);
  const [poster, setPoster] = useState(cmsPoster);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (cmsPoster) {
      setPoster(cmsPoster);
      setFailed(false);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      const ytId = getYouTubeVideoId(videoUrl);
      if (ytId) {
        if (!cancelled) setPoster(`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`);
        return;
      }

      const vimeoId = getVimeoVideoId(videoUrl);
      if (vimeoId) {
        try {
          const res = await fetch(
            `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${vimeoId}`
          );
          if (res.ok) {
            const data = await res.json();
            if (!cancelled && data?.thumbnail_url) {
              setPoster(data.thumbnail_url);
              return;
            }
          }
        } catch {
          /* fallback mai jos */
        }
      }

      if (isNativeVideoFile(videoUrl)) {
        const frame = await captureNativeVideoFrame(videoUrl);
        if (!cancelled && frame) setPoster(frame);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [videoUrl, cmsPoster]);

  const showPoster = Boolean(poster) && !failed;

  return (
    <SoftBox
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        bgcolor: "#0c1f32",
        overflow: "hidden",
      }}
    >
      {showPoster ? (
        <SoftBox
          component="img"
          src={poster}
          alt=""
          onError={() => setFailed(true)}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : (
        <SoftBox
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(145deg, rgba(20,42,63,0.95) 0%, rgba(6,29,54,0.98) 55%, rgba(10,61,92,0.85) 100%)",
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 0,
              backgroundImage:
                "repeating-linear-gradient(-45deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 8px)",
            },
          }}
        />
      )}
      <SoftBox
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: showPoster ? "rgba(0,0,0,0.28)" : "rgba(0,0,0,0.12)",
        }}
      >
        <SoftBox
          sx={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.94)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 10px rgba(0,0,0,0.45)",
          }}
        >
          <Icon sx={{ fontSize: "0.9rem !important", color: "#061D36", ml: "1px" }}>
            play_arrow
          </Icon>
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}

/**
 * Imaginea mare a unui slide. Fișierul se cere de la server abia când slide-ul a fost
 * afișat cel puțin o dată (`shouldLoad`); până atunci se arată thumbnail-ul base64
 * (deja livrat de API) blurat, ca placeholder, ca să nu existe zonă goală.
 */
function CarouselSlideImage({ shouldLoad, src, thumbnail, priority, imgSx }) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
  }, [src]);

  return (
    <>
      {thumbnail && !loaded && (
        <SoftBox
          component="img"
          src={thumbnail}
          alt=""
          aria-hidden="true"
          sx={{
            ...imgSx,
            position: "absolute",
            top: 0,
            left: 0,
            filter: "blur(16px)",
            transform: "scale(1.04)",
          }}
        />
      )}
      {shouldLoad && (
        <SoftBox
          component="img"
          src={src}
          alt=""
          decoding="async"
          fetchpriority={priority ? "high" : "auto"}
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
          sx={{
            ...imgSx,
            position: "relative",
            opacity: loaded ? 1 : 0,
            transition: "opacity 0.4s ease",
          }}
        />
      )}
    </>
  );
}

/** Placeholder pentru slide-urile video neîncărcate încă (poster din CMS, fără iframe/video). */
function CarouselVideoPlaceholder({ poster }) {
  return (
    <SoftBox sx={{ width: "100%", height: "100%", position: "relative", bgcolor: "#0c1f32" }}>
      {poster && (
        <SoftBox
          component="img"
          src={poster}
          alt=""
          aria-hidden="true"
          sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      )}
    </SoftBox>
  );
}

/**
 * Video MP4/WebM: pornește o dată la activarea slide-ului; după sfârșit sau pauză utilizator,
 * rămâne oprit și pornește timerul pentru următorul slide. Nu ține cont de hover-ul pe carusel.
 */
function CarouselNativeVideo({ isActive, src, title, onAdvance }) {
  const ref = useRef(null);
  const postStopTimerRef = useRef(null);
  const seekingRef = useRef(false);
  const playedOnceRef = useRef(false);

  const clearPostStopTimer = useCallback(() => {
    if (postStopTimerRef.current) {
      clearTimeout(postStopTimerRef.current);
      postStopTimerRef.current = null;
    }
  }, []);

  const scheduleAdvanceAfterStop = useCallback(() => {
    clearPostStopTimer();
    if (!isActive) return;
    postStopTimerRef.current = setTimeout(() => {
      postStopTimerRef.current = null;
      onAdvance();
    }, AUTOPLAY_INTERVAL);
  }, [isActive, onAdvance, clearPostStopTimer]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!isActive) {
      el.pause();
      clearPostStopTimer();
      playedOnceRef.current = false;
      try {
        el.currentTime = 0;
      } catch {
        /* ignore */
      }
      return;
    }
    if (el.ended) {
      return;
    }
    playedOnceRef.current = false;
    el.muted = true;
    const p = el.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }, [isActive, src, clearPostStopTimer]);

  useEffect(() => () => clearPostStopTimer(), [clearPostStopTimer]);

  return (
    <video
      ref={ref}
      src={src}
      style={{ width: "100%", height: "100%", objectFit: "cover" }}
      controls
      playsInline
      preload="auto"
      title={title}
      onPlaying={() => {
        playedOnceRef.current = true;
      }}
      onPlay={clearPostStopTimer}
      onEnded={scheduleAdvanceAfterStop}
      onSeeking={() => {
        seekingRef.current = true;
      }}
      onSeeked={() => {
        seekingRef.current = false;
      }}
      onPause={() => {
        if (!isActive) return;
        setTimeout(() => {
          const el = ref.current;
          if (!el || seekingRef.current) return;
          if (el.ended) return;
          if (!playedOnceRef.current) return;
          if (!el.paused) return;
          scheduleAdvanceAfterStop();
        }, 500);
      }}
    />
  );
}

function HeroSectionCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [slides, setSlides] = useState([]);
  // Indexul slide-urilor al căror media a fost deja cerut de la server. Slide-urile
  // rămân montate după prima vizionare, ca revenirea la ele să fie instantanee.
  const [visitedSlides, setVisitedSlides] = useState(() => new Set([0]));
  const isDesktopCarousel = useMediaQuery(DESKTOP_CAROUSEL_QUERY);

  const client = useAPI();
  const clientRef = useRef(client);
  clientRef.current = client;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [data, thumbnailsData] = await Promise.all([
          clientRef.current.getCarouselContent(),
          clientRef.current.getCarouselThumbnails().catch(() => []),
        ]);
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (data?.results ?? []);
        const thumbList = Array.isArray(thumbnailsData)
          ? thumbnailsData
          : (thumbnailsData?.results ?? []);
        // Îmbină thumbnail-urile (base64) cu slide-urile, poziție cu poziție.
        const merged = list.map((slide, i) => ({
          ...slide,
          thumbnail: thumbList[i]?.thumbnail ?? slide.thumbnail ?? null,
        }));
        console.log(merged);
        if (merged.length > 0) {
          setSlides(merged);
          setActive(0);
        }
      } catch (error) {
        console.error("Error fetching carousel content:", error);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const next = useCallback(() => setActive((p) => (p + 1) % slides.length), [slides.length]);
  const prev = useCallback(() => setActive((p) => (p - 1 + slides.length) % slides.length), [slides.length]);

  useEffect(() => {
    setVisitedSlides((visited) => {
      if (visited.has(active)) return visited;
      const updated = new Set(visited);
      updated.add(active);
      return updated;
    });
  }, [active]);

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const cur = slides[active];
    const isNativeFileVideo = cur?.type === "video" && isNativeVideoFile(cur?.videoUrl ?? "");
    const isEmbedVideo = cur?.type === "video" && !isNativeFileVideo;
    if (isNativeFileVideo) return;
    const delay = isEmbedVideo ? EMBED_VIDEO_INTERVAL_MS : AUTOPLAY_INTERVAL;
    const id = setInterval(next, delay);
    return () => clearInterval(id);
  }, [paused, next, slides.length, active, slides]);

  if (slides.length === 0) return null;

  // Helperi pentru blocurile editoriale (badge + titlu + descriere + CTA),
  // folosiți identic în slide-urile imagine și video pentru un feel unitar.
  const editorialBadge = (label) => (
    <SoftBox display="flex" alignItems="center" mb={isDesktopCarousel ? { xs: 2, md: 2.5 } : { xs: 1.75, sm: 2 }}>
      <SoftBox
        sx={{
          width: isDesktopCarousel ? { xs: "32px", md: "44px" } : { xs: "32px", sm: "36px" },
          height: "3px",
          background: "#fff",
          borderRadius: "2px",
          mr: 1.75,
          flexShrink: 0,
        }}
      />
      <SoftTypography
        sx={{
          color: "rgba(255,255,255,0.92)",
          letterSpacing: "3px",
          fontSize: isDesktopCarousel ? { xs: "0.7rem", md: "0.78rem" } : { xs: "0.7rem", sm: "0.72rem" },
          fontWeight: 700,
          textTransform: "uppercase",
        }}
      >
        {label}
      </SoftTypography>
    </SoftBox>
  );

  const editorialTitle = (text) => (
    <SoftTypography
      sx={{
        color: "#fff",
        fontWeight: 800,
        mb: isDesktopCarousel ? { xs: 2, md: 2.5 } : { xs: 1.5, sm: 1.75 },
        lineHeight: 1.1,
        fontSize: isDesktopCarousel
          ? { xs: "1.75rem", sm: "2.1rem", md: "2.75rem", lg: "3.1rem" }
          : { xs: "1.75rem", sm: "2rem" },
        letterSpacing: "-0.02em",
        textShadow: isDesktopCarousel
          ? { xs: "0 2px 12px rgba(0,0,0,0.5)", md: "none" }
          : "0 2px 12px rgba(0,0,0,0.5)",
      }}
    >
      {text}
    </SoftTypography>
  );

  const editorialDescription = (text) => (
    <SoftTypography
      sx={{
        color: "rgba(255,255,255,0.88)",
        lineHeight: 1.65,
        fontWeight: 300,
        mb: isDesktopCarousel ? { xs: 3, md: 3.5 } : { xs: 2.25, sm: 2.5 },
        fontSize: isDesktopCarousel ? { xs: "0.98rem", md: "1.08rem" } : { xs: "0.98rem", sm: "1rem" },
        textShadow: isDesktopCarousel
          ? { xs: "0 1px 6px rgba(0,0,0,0.4)", md: "none" }
          : "0 1px 6px rgba(0,0,0,0.4)",
      }}
    >
      {text}
    </SoftTypography>
  );

  const slideCtaLabel = (slide) => {
    const raw = slide?.linkLabel ?? slide?.link_label;
    if (raw == null || raw === "") return "";
    return String(raw).trim();
  };

  const editorialCTA = (link, linkLabel) => {
    const label = typeof linkLabel === "string" ? linkLabel.trim() : "";
    if (!label) return null;

    return (
    <SoftButton
      component="a"
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      size="large"
      sx={{
        background: "#FFC107 !important",
        color: "#0a3d5c !important",
        fontWeight: 700,
        px: 4,
        py: 1.7,
        borderRadius: "10px",
        textTransform: "none",
        fontSize: isDesktopCarousel ? { xs: "0.92rem", md: "1rem" } : { xs: "0.92rem", sm: "0.94rem" },
        letterSpacing: "0.01em",
        boxShadow: "0 10px 28px rgba(0,0,0,0.35)",
        ...(isDesktopCarousel ? {} : { px: 3.5, py: 1.45 }),
        "&:hover": {
          background: "#FFB300 !important",
          color: "#0a3d5c !important",
          transform: "translateY(-2px)",
          boxShadow: "0 14px 36px rgba(0,0,0,0.45)",
        },
        "&:focus": { background: "#FFC107 !important", color: "#0a3d5c !important" },
        "&:focus:not(:hover)": { background: "#FFC107 !important", color: "#0a3d5c !important" },
        "&:active": { background: "#FFB300 !important", color: "#0a3d5c !important" },
        "&.Mui-focusVisible": { background: "#FFC107 !important", color: "#0a3d5c !important" },
        "& .MuiTouchRipple-root": { display: "none" },
        transition: "all 0.3s ease",
      }}
    >
      {label}
      <Icon sx={{ ml: 1.25, fontSize: "1.35rem !important" }}>arrow_forward</Icon>
    </SoftButton>
    );
  };

  return (
    <SoftBox
      component="section"
      width="100%"
      sx={{
        position: "relative",
        overflow: "hidden",
        background: "#061D36",
      }}
    >
      {/* Stage: containerul tuturor slide-urilor, ocupă tot hero-ul */}
      <SoftBox
        sx={{
          position: "relative",
          width: "100%",
          minHeight: isDesktopCarousel ? { md: "500px", lg: "540px" } : "auto",
        }}
      >
        {slides.map((s, i) => {
          const isActive = i === active;
          const shouldLoadMedia = isActive || visitedSlides.has(i);
          const slideShellSx = isDesktopCarousel
            ? {
                position: "absolute",
                inset: 0,
                opacity: isActive ? 1 : 0,
                visibility: isActive ? "visible" : "hidden",
                pointerEvents: isActive ? "auto" : "none",
                transition: "opacity 0.6s ease",
              }
            : {
                display: isActive ? "block" : "none",
                width: "100%",
              };

          return (
            <SoftBox key={i} sx={slideShellSx} aria-hidden={!isActive}>
              {/* SLIDE TIP IMAGINE — media bleed-uit pe stânga cu margine dreaptă rotunjită */}
              {s.type === "image" && (
                <SoftBox sx={{ position: "relative", width: "100%", ...(isDesktopCarousel ? { height: "100%" } : {}) }}>
                  {/* MEDIA — absolute pe desktop, 16:9 in-flow pe mobile/tabletă */}
                  <SoftBox
                    sx={{
                      ...(isDesktopCarousel
                        ? {
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            width: { md: "58%", lg: "55%" },
                            maxWidth: { lg: "920px" },
                            height: "auto",
                            borderRadius: 0,
                            "@media (min-width:756px)": { top: "1em" },
                          }
                        : {
                            position: "relative",
                            width: "100%",
                            aspectRatio: "16 / 9",
                            borderRadius: "0 0 28px 28px",
                          }),
                      overflow: "hidden",
                      bgcolor: s.imageFallback ? "rgba(255,255,255,0.06)" : "#061D36",
                    }}
                  >
                    <CarouselSlideImage
                      shouldLoad={shouldLoadMedia}
                      src={s.image}
                      thumbnail={s.thumbnail}
                      priority={i === 0}
                      imgSx={{
                        width: "100%",
                        height: "100%",
                        objectFit: s.imageFallback ? "contain" : "cover",
                        objectPosition: "top",
                        p: s.imageFallback ? { xs: 3, md: 6 } : 0,
                        display: "block",
                        ...(isDesktopCarousel && {
                          "@media (min-width:756px)": {
                            width: "95%",
                            height: "95%",
                            objectPosition: "top",
                          },
                        }),
                      }}
                    />
                    {/* CURBĂ ORGANICĂ — overlay PNG cu zone transparente, definește
                       marginea dreaptă curbată a fotografiei pe desktop. Pe mobile e
                       ascuns (imaginea ocupă toată lățimea, fără curbă). PNG-ul are
                       navy solid în dreapta, deci marginea sa dreaptă „mănâncă" curat
                       fotografia, fără să fie nevoie de clip-path. */}
                    {!s.imageFallback && (
                      <SoftBox
                        aria-hidden="true"
                        sx={{
                          position: "absolute",
                          right: 0,
                          top: 0,
                          bottom: 0,
                          backgroundImage: `url(${rightCurveOverlay})`,
                          backgroundSize: "100% 100%",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "right center",
                          pointerEvents: "none",
                          display: isDesktopCarousel ? "block" : "none",
                          width: { md: "20%", lg: "25%" },
                          zIndex: 1,
                        }}
                      />
                    )}
                    {/* FRIEZĂ DECORATIVĂ — peste imagine, la bază (mâini ridicate + accente aurii).
                        Imaginea PNG are deja transparență naturală în top (val curbat),
                        așa că lasă fotografia să se vadă prin val. Punte de navy între
                        fotografie și frieză (gradient) pentru cazurile în care imaginea
                        nu se extinde până jos. Frieza moștenește clip-ul (mask-image)
                        părintelui, deci respectă curba concavă din dreapta. */}
                    {!s.imageFallback && (
                      <>
                        {/* Frieza propriu-zisă — împinsă puțin sub linia hero-ului cu
                           `bottom` negativ, astfel încât decorațiile tăiate de la
                           marginea de sus a PNG-ului să iasă în jos din vizual.
                           zIndex 3 ca să stea peste overlay-ul de curbă din dreapta. */}
                        <SoftBox
                          aria-hidden="true"
                          sx={{
                            position: "absolute",
                            left: isDesktopCarousel ? { md: "-5em" } : 0,
                            right: 0,
                            bottom: isDesktopCarousel
                              ? { md: "-50px", lg: "-60px" }
                              : { xs: "-27px", sm: "-65px" },
                            height: isDesktopCarousel
                              ? { md: "180px", lg: "220px" }
                              : { xs: "120px", sm: "255px" },
                            backgroundImage: `url(${handsFriezeOrnament})`,
                            backgroundSize: "cover",
                            backgroundPosition: "bottom center",
                            backgroundRepeat: "no-repeat",
                            pointerEvents: "none",
                            zIndex: 3,
                          }}
                        />
                      </>
                    )}
                  </SoftBox>

                  {/* TEXT — pe dreapta într-un grid normal */}
                  <Container
                    maxWidth="xl"
                    sx={{
                      position: "relative",
                      px: { xs: 2.5, md: 5 },
                      height: isDesktopCarousel ? { md: "100%" } : undefined,
                      zIndex: 2,
                      pointerEvents: "none",
                    }}
                  >
                    <Grid container alignItems="center" sx={{ minHeight: isDesktopCarousel ? { md: "100%" } : undefined }}>
                      <Grid
                        item
                        xs={12}
                        {...(isDesktopCarousel ? { md: 5 } : {})}
                        sx={{
                          ml: isDesktopCarousel ? { md: "auto" } : undefined,
                          py: isDesktopCarousel ? { xs: 4, md: 4 } : STACKED_TEXT_PY,
                          pr: isDesktopCarousel ? { md: 6 } : undefined,
                          pointerEvents: "auto",
                        }}
                      >
                        {editorialBadge(s.label)}
                        {editorialTitle(s.title)}
                        {editorialDescription(s.text)}
                        {editorialCTA(s.link, slideCtaLabel(s))}
                      </Grid>
                    </Grid>
                  </Container>
                </SoftBox>
              )}

              {/* SLIDE TIP VIDEO — aceeași structură: media stânga (rounded) + text dreapta */}
              {s.type === "video" && (
                <SoftBox sx={{ position: "relative", width: "100%", ...(isDesktopCarousel ? { height: "100%" } : {}) }}>
                  <SoftBox
                    sx={{
                      ...(isDesktopCarousel
                        ? {
                            position: "absolute",
                            top: { md: 0 },
                            bottom: { md: 0 },
                            left: { md: 0 },
                            width: { md: "58%", lg: "55%" },
                            maxWidth: { lg: "920px" },
                            height: "auto",
                            borderRadius: 0,
                          }
                        : {
                            position: "relative",
                            width: "100%",
                            aspectRatio: "16 / 9",
                            borderRadius: "0 0 28px 28px",
                          }),
                      overflow: "hidden",
                      bgcolor: "rgba(0,0,0,0.4)",
                    }}
                  >
                    {!shouldLoadMedia && <CarouselVideoPlaceholder poster={getSlidePosterUrl(s)} />}
                    {shouldLoadMedia &&
                      (isNativeVideoFile(s.videoUrl) ? (
                        <CarouselNativeVideo
                          isActive={isActive}
                          src={s.videoUrl}
                          title={s.title}
                          onAdvance={next}
                        />
                      ) : (
                        <iframe
                          src={s.videoUrl}
                          style={{ width: "100%", height: "100%", border: "none" }}
                          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                          allowFullScreen
                          title={s.title}
                        />
                      ))}
                  </SoftBox>

                  <Container
                    maxWidth="xl"
                    sx={{
                      position: "relative",
                      px: { xs: 2.5, md: 5 },
                      height: isDesktopCarousel ? { md: "100%" } : undefined,
                      zIndex: 2,
                      pointerEvents: "none",
                    }}
                  >
                    <Grid container alignItems="center" sx={{ minHeight: isDesktopCarousel ? { md: "100%" } : undefined }}>
                      <Grid
                        item
                        xs={12}
                        {...(isDesktopCarousel ? { md: 5 } : {})}
                        sx={{
                          ml: isDesktopCarousel ? { md: "auto" } : undefined,
                          py: isDesktopCarousel ? { xs: 4, md: 4 } : STACKED_TEXT_PY,
                          pr: isDesktopCarousel ? { md: 6 } : undefined,
                          pointerEvents: "auto",
                        }}
                      >
                        {editorialBadge(s.label)}
                        {editorialTitle(s.title)}
                        {editorialDescription(s.text)}
                        {editorialCTA(s.link, slideCtaLabel(s))}
                      </Grid>
                    </Grid>
                  </Container>
                </SoftBox>
              )}

              {/* SLIDE TIP TEXT — citat editorial centrat */}
              {s.type === "text" && (
                <SoftBox sx={{ position: "relative", width: "100%", ...(isDesktopCarousel ? { height: "100%" } : {}) }}>
                  <SoftBox
                    aria-hidden="true"
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "radial-gradient(ellipse at 50% 30%, rgba(0,86,134,0.45) 0%, transparent 65%), linear-gradient(135deg, #061D36 0%, #0a3d5c 100%)",
                    }}
                  />
                  <Container
                    maxWidth="lg"
                    sx={{
                      position: "relative",
                      height: isDesktopCarousel ? "100%" : "auto",
                      px: { xs: 3, md: 5 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      py: isDesktopCarousel ? { xs: 6, md: 0 } : STACKED_TEXT_PY,
                    }}
                  >
                    <SoftBox sx={{ textAlign: "center", position: "relative", maxWidth: "900px", mx: "auto" }}>
                      {/* Ghilimea decorativă uriașă */}
                      <SoftBox
                        aria-hidden="true"
                        sx={{
                          position: "absolute",
                          top: { xs: -40, md: -90 },
                          left: "50%",
                          transform: "translateX(-50%)",
                          fontSize: { xs: "120px", md: "240px" },
                          color: "rgba(255,255,255,0.06)",
                          lineHeight: 1,
                          fontFamily: "Georgia, serif",
                          pointerEvents: "none",
                          userSelect: "none",
                        }}
                      >
                        “
                      </SoftBox>
                      <SoftBox display="flex" alignItems="center" justifyContent="center" mb={{ xs: 3, md: 4 }} sx={{ position: "relative", zIndex: 1 }}>
                        <SoftBox sx={{ width: { xs: "32px", md: "56px" }, height: "2px", background: "rgba(255,255,255,0.55)", borderRadius: "2px", mr: 2, flexShrink: 0 }} />
                        <SoftTypography sx={{ color: "rgba(255,255,255,0.78)", letterSpacing: "4px", fontSize: { xs: "0.72rem", md: "0.85rem" }, fontWeight: 700, textTransform: "uppercase" }}>
                          Stop Rasism
                        </SoftTypography>
                        <SoftBox sx={{ width: { xs: "32px", md: "56px" }, height: "2px", background: "rgba(255,255,255,0.55)", borderRadius: "2px", ml: 2, flexShrink: 0 }} />
                      </SoftBox>
                      <SoftTypography
                        sx={{
                          fontSize: { xs: "1.2rem", sm: "1.45rem", md: "1.75rem", lg: "1.9rem" },
                          color: "#fff",
                          lineHeight: { xs: 1.55, md: 1.45 },
                          fontWeight: 300,
                          fontStyle: "italic",
                          letterSpacing: "-0.005em",
                          position: "relative",
                          zIndex: 1,
                          textShadow: "0 2px 18px rgba(0,0,0,0.3)",
                        }}
                      >
                        {s.text}
                      </SoftTypography>
                    </SoftBox>
                  </Container>
                </SoftBox>
              )}
            </SoftBox>
          );
        })}

        {/* Săgeți de navigare — în margini pe desktop, jos pe mobile */}
        <SoftBox
          component="button"
          type="button"
          onClick={prev}
          aria-label="Slide anterior"
          sx={{
            position: "absolute",
            top: "50%",
            left: { xs: 12, md: 24 },
            transform: "translateY(-50%)",
            width: { xs: 40, md: 52 },
            height: { xs: 40, md: 52 },
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.35)",
            background: "rgba(6,29,54,0.5)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            display: isDesktopCarousel ? { xs: "none", sm: "flex" } : "none",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            p: 0,
            zIndex: 5,
            "&:hover": { background: "rgba(6,29,54,0.8)", borderColor: "rgba(255,255,255,0.7)", transform: "translateY(-50%) scale(1.05)" },
            "&:focus-visible": { outline: "2px solid #fff", outlineOffset: 2 },
            transition: "all 0.2s",
          }}
        >
          <Icon sx={{ color: "#fff", fontSize: { xs: "1.4rem !important", md: "1.7rem !important" } }}>chevron_left</Icon>
        </SoftBox>

        <SoftBox
          component="button"
          type="button"
          onClick={next}
          aria-label="Slide următor"
          sx={{
            position: "absolute",
            top: "50%",
            right: { xs: 12, md: 24 },
            transform: "translateY(-50%)",
            width: { xs: 40, md: 52 },
            height: { xs: 40, md: 52 },
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.35)",
            background: "rgba(6,29,54,0.5)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            display: isDesktopCarousel ? { xs: "none", sm: "flex" } : "none",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            p: 0,
            zIndex: 5,
            "&:hover": { background: "rgba(6,29,54,0.8)", borderColor: "rgba(255,255,255,0.7)", transform: "translateY(-50%) scale(1.05)" },
            "&:focus-visible": { outline: "2px solid #fff", outlineOffset: 2 },
            transition: "all 0.2s",
          }}
        >
          <Icon sx={{ color: "#fff", fontSize: { xs: "1.4rem !important", md: "1.7rem !important" } }}>chevron_right</Icon>
        </SoftBox>

      </SoftBox>

      {/* ──────────────────────────────────────────────────────────────────────
         BANDĂ DE NAVIGARE — sub burtieră
         Fundal navy curat, thumbnails de slide-uri în stânga, dots în centru,
         indicator numeric în dreapta. Fără badge confidențialitate.
      ────────────────────────────────────────────────────────────────────── */}
      <SoftBox
        role="group"
        aria-label="Navigare carusel"
        sx={{
          position: "relative",
          width: "100%",
          backgroundColor: "#0a1828",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          py: { xs: 1.5, md: 2.25 },
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 } }}>
          <Grid container alignItems="center" spacing={{ xs: 1.5, md: 2.5 }}>
            {/* STÂNGA — thumbnails de slide-uri (doar desktop ≥1025px) */}
            {isDesktopCarousel && (
            <Grid
              item
              xs={false}
              md
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                gap: 1,
                flexWrap: "nowrap",
                overflowX: "auto",
                pb: 0.5,
                // Scrollbar discret
                "&::-webkit-scrollbar": { height: "4px" },
                "&::-webkit-scrollbar-thumb": {
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: "2px",
                },
              }}
            >
              {slides.map((s, i) => {
                const isCurrent = i === active;
                return (
                  <SoftBox
                    key={`thumb-${i}`}
                    component="button"
                    type="button"
                    onClick={() => setActive(i)}
                    aria-label={`Mergi la slide-ul ${i + 1}`}
                    aria-current={isCurrent ? "true" : undefined}
                    sx={{
                      flexShrink: 0,
                      width: { md: "76px", lg: "86px" },
                      height: { md: "48px", lg: "54px" },
                      borderRadius: "8px",
                      overflow: "hidden",
                      position: "relative",
                      border: isCurrent ? "2px solid #f5b400" : "2px solid transparent",
                      boxShadow: isCurrent ? "0 4px 14px rgba(0,0,0,0.5)" : "none",
                      cursor: "pointer",
                      p: 0,
                      transition: "all 0.25s ease",
                      bgcolor: "#13283d",
                      opacity: isCurrent ? 1 : 0.65,
                      "&:hover": { opacity: 1, transform: "translateY(-1px)" },
                      "&:focus-visible": { outline: "2px solid #fff", outlineOffset: 2 },
                    }}
                  >
                    {/* Conținut thumbnail după tip — preferă thumbnail-ul base64 din API */}
                    {s.type === "image" && (s.thumbnail || s.image) && (
                      <SoftBox
                        component="img"
                        src={s.thumbnail || s.image}
                        alt=""
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: s.imageFallback && !s.thumbnail ? "contain" : "cover",
                          p: s.imageFallback && !s.thumbnail ? "6px" : 0,
                          display: "block",
                        }}
                      />
                    )}
                    {s.type === "video" && <CarouselVideoThumbnail slide={s} />}
                    {s.type === "text" && s.thumbnail && (
                      <SoftBox
                        component="img"
                        src={s.thumbnail}
                        alt=""
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    )}
                    {s.type === "text" && !s.thumbnail && (
                      <SoftBox
                        sx={{
                          width: "100%",
                          height: "100%",
                          background:
                            "linear-gradient(135deg, #1a3548 0%, #25556e 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontFamily: "Georgia, serif",
                          color: "rgba(255,255,255,0.88)",
                          fontSize: "1.4rem",
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        “
                      </SoftBox>
                    )}
                  </SoftBox>
                );
              })}
            </Grid>
            )}

            {/* CENTRU — dots (toate ecranele) */}
            <Grid
              item
              xs={12}
              md={isDesktopCarousel ? "auto" : 12}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 1.25, md: 1.5 },
              }}
            >
              {/* Săgeți stânga/dreapta — mobile + tabletă (≤1024px) */}
              {!isDesktopCarousel && (
              <SoftBox
                component="button"
                type="button"
                onClick={prev}
                aria-label="Slide anterior"
                sx={{
                  display: "flex",
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  border: "1px solid rgba(255,255,255,0.35)",
                  background: "transparent",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  p: 0,
                }}
              >
                <Icon sx={{ color: "#fff", fontSize: "1.2rem !important" }}>chevron_left</Icon>
              </SoftBox>
              )}

              <SoftBox display="flex" alignItems="center" gap={1}>
                {slides.map((_, i) => (
                  <SoftBox
                    key={`dot-${i}`}
                    component="button"
                    type="button"
                    onClick={() => setActive(i)}
                    aria-label={`Mergi la slide-ul ${i + 1}`}
                    aria-current={i === active ? "true" : undefined}
                    sx={{
                      width: i === active ? { xs: "26px", md: "32px" } : { xs: "9px", md: "10px" },
                      height: { xs: "9px", md: "10px" },
                      borderRadius: "999px",
                      bgcolor: i === active ? "#f5b400" : "rgba(255,255,255,0.32)",
                      cursor: "pointer",
                      border: "none",
                      p: 0,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        bgcolor: i === active ? "#f5b400" : "rgba(255,255,255,0.6)",
                      },
                      "&:focus-visible": { outline: "2px solid #fff", outlineOffset: 2 },
                    }}
                  />
                ))}
              </SoftBox>

              {/* Săgeată dreapta — mobile + tabletă (≤1024px) */}
              {!isDesktopCarousel && (
              <SoftBox
                component="button"
                type="button"
                onClick={next}
                aria-label="Slide următor"
                sx={{
                  display: "flex",
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  border: "1px solid rgba(255,255,255,0.35)",
                  background: "transparent",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  p: 0,
                }}
              >
                <Icon sx={{ color: "#fff", fontSize: "1.2rem !important" }}>chevron_right</Icon>
              </SoftBox>
              )}
            </Grid>

            {/* DREAPTA — indicator numeric (doar desktop ≥1025px) */}
            {isDesktopCarousel && (
            <Grid
              item
              md="auto"
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                justifyContent: "flex-end",
                minWidth: { md: "120px", lg: "140px" },
              }}
            >
              <SoftTypography
                sx={{
                  color: "rgba(255,255,255,0.95)",
                  fontSize: { md: "1rem", lg: "1.05rem" },
                  fontWeight: 700,
                  letterSpacing: "0.18em",
                  fontVariantNumeric: "tabular-nums",
                }}
                aria-hidden="true"
              >
                {String(active + 1).padStart(2, "0")}{" "}
                <span style={{ opacity: 0.5, fontWeight: 500 }}>
                  / {String(slides.length).padStart(2, "0")}
                </span>
              </SoftTypography>
            </Grid>
            )}
          </Grid>
        </Container>
      </SoftBox>
    </SoftBox>
  );
}

export default HeroSectionCarousel;
